import ExcelJS from "exceljs";
import * as XLSX from "xlsx";

const HEADER_ALIASES = {
  item_name: [
    "ten hang",
    "ten hang *",
    "ten mat hang",
    "mat hang",
    "hang hoa",
    "item name",
    "name",
    "mo ta",
  ],
  unit: ["don vi", "dvt", "unit", "uom"],
  quantity: ["so luong", "so luong *", "sl", "qty", "quantity"],
  unit_price: [
    "don gia",
    "don gia *",
    "gia",
    "unit price",
    "price",
    "don gia truoc vat",
  ],
  note: ["ghi chu", "note", "ghichu"],
};

const normalizeHeader = (v) =>
  String(v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const parseNumber = (raw) => {
  if (raw == null || raw === "") return NaN;
  if (typeof raw === "number") return raw;
  let s = String(raw).trim().replace(/\s/g, "");
  // 1.234.567,89 or 1,234,567.89
  if (/\d,\d{1,2}$/.test(s) && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (/\d\.\d{1,2}$/.test(s) && s.includes(",")) {
    s = s.replace(/,/g, "");
  } else {
    s = s.replace(/,/g, "");
  }
  s = s.replace(/[^\d.-]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

function mapHeaders(row) {
  const map = {};
  row.forEach((cell, idx) => {
    const key = normalizeHeader(cell);
    if (!key) return;
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.includes(key) && map[field] == null) {
        map[field] = idx;
      }
    }
  });
  return map;
}

/**
 * Parse file Excel/CSV → { items, errors }
 * items: { item_name, unit, quantity, unit_price, amount, note }[]
 */
export async function parseInvoiceItemsFile(file) {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array", cellDates: true });
  const sheetName =
    wb.SheetNames.find((n) => /mathang|mat hang|items|data/i.test(n)) ||
    wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  if (!sheet) {
    return { items: [], errors: ["Không đọc được sheet Excel."] };
  }

  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: true,
  });
  if (!rows.length) {
    return { items: [], errors: ["File trống."] };
  }

  let headerRowIdx = 0;
  let colMap = mapHeaders(rows[0] || []);
  if (colMap.item_name == null) {
    for (let i = 1; i < Math.min(rows.length, 8); i += 1) {
      const m = mapHeaders(rows[i] || []);
      if (m.item_name != null) {
        headerRowIdx = i;
        colMap = m;
        break;
      }
    }
  }

  if (colMap.item_name == null || colMap.quantity == null || colMap.unit_price == null) {
    return {
      items: [],
      errors: [
        "Thiếu cột bắt buộc. Cần: Tên hàng, Số lượng, Đơn giá (xem file mẫu).",
      ],
    };
  }

  const items = [];
  const errors = [];

  for (let r = headerRowIdx + 1; r < rows.length; r += 1) {
    const row = rows[r] || [];
    const isEmpty = row.every(
      (c) => c == null || String(c).trim() === ""
    );
    if (isEmpty) continue;

    const item_name = String(row[colMap.item_name] ?? "").trim();
    const unit =
      colMap.unit != null ? String(row[colMap.unit] ?? "").trim() : "";
    const quantity = parseNumber(row[colMap.quantity]);
    const unit_price = parseNumber(row[colMap.unit_price]);
    const note =
      colMap.note != null ? String(row[colMap.note] ?? "").trim() : "";

    if (!item_name && Number.isNaN(quantity) && Number.isNaN(unit_price)) {
      continue;
    }

    const excelRow = r + 1;
    if (!item_name) {
      errors.push(`Dòng ${excelRow}: thiếu tên hàng.`);
      continue;
    }
    if (!(quantity > 0)) {
      errors.push(`Dòng ${excelRow}: số lượng phải > 0.`);
      continue;
    }
    if (Number.isNaN(unit_price) || unit_price < 0) {
      errors.push(`Dòng ${excelRow}: đơn giá không hợp lệ.`);
      continue;
    }

    const amount = Math.round(quantity * unit_price);
    items.push({
      key: `import-${excelRow}-${Date.now()}-${items.length}`,
      item_name,
      unit,
      quantity: String(quantity),
      unit_price: String(Math.round(unit_price)),
      amount: String(amount),
      note,
    });
  }

  return { items, errors };
}

export async function downloadInvoiceItemsTemplate() {
  const wb = new ExcelJS.Workbook();
  wb.creator = "IMA Vietnam ERP";
  wb.created = new Date();

  const guide = wb.addWorksheet("HuongDan");
  guide.getColumn(1).width = 88;
  guide.addRow(["Mẫu nhập mặt hàng hóa đơn — IMA ERP"]);
  guide.getRow(1).font = { bold: true, size: 14, color: { argb: "FFB91C1C" } };
  guide.addRow([]);
  guide.addRow(["1. Điền dữ liệu vào sheet MatHang (không đổi tên cột dòng 1)."]);
  guide.addRow(["2. Cột bắt buộc: Tên hàng, Số lượng, Đơn giá (trước VAT)."]);
  guide.addRow(["3. Thành tiền = Số lượng × Đơn giá (App tự tính khi import)."]);
  guide.addRow(["4. Lưu .xlsx và chọn «Nhập từ Excel» trong form ghi nhận HĐ."]);
  guide.addRow(["5. File .xls / .csv cũng được hỗ trợ."]);

  const ws = wb.addWorksheet("MatHang");
  const headers = ["Tên hàng *", "Đơn vị", "Số lượng *", "Đơn giá *", "Ghi chú"];
  ws.addRow(headers);
  ws.getRow(1).font = { bold: true };
  ws.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFEE2E2" },
  };
  ws.columns = [
    { width: 36 },
    { width: 12 },
    { width: 14 },
    { width: 16 },
    { width: 28 },
  ];
  ws.addRow(["Giấy A4 70gsm", "ram", 10, 85000, "Ví dụ"]);
  ws.addRow(["Bút bi xanh", "cây", 50, 3500, ""]);

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "mau_mat_hang_hoa_don.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

export function emptyInvoiceItem(partial = {}) {
  return {
    key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    item_name: "",
    unit: "",
    quantity: "1",
    unit_price: "",
    amount: "",
    note: "",
    ...partial,
  };
}

export function calcItemAmount(quantity, unitPrice) {
  const q = Number(quantity);
  const p = Number(unitPrice);
  if (!(q > 0) || Number.isNaN(p) || p < 0) return 0;
  return Math.round(q * p);
}

export function sumItemsSubtotal(items = []) {
  return (items || []).reduce(
    (acc, row) => acc + calcItemAmount(row.quantity, row.unit_price),
    0
  );
}

export function mapApiItemsToForm(apiItems = []) {
  return (apiItems || []).map((row, idx) => ({
    key: row.id || `api-${idx}`,
    item_name: row.item_name || "",
    unit: row.unit || "",
    quantity: row.quantity != null ? String(row.quantity) : "1",
    unit_price:
      row.unit_price != null ? String(Math.round(Number(row.unit_price))) : "",
    amount:
      row.amount != null
        ? String(Math.round(Number(row.amount)))
        : String(calcItemAmount(row.quantity, row.unit_price)),
    note: row.note || "",
  }));
}

export function formItemsToPayload(items = []) {
  return (items || [])
    .map((row) => {
      const item_name = String(row.item_name || "").trim();
      if (!item_name) return null;
      const quantity = Number(row.quantity);
      const unit_price = Number(row.unit_price);
      if (!(quantity > 0) || Number.isNaN(unit_price) || unit_price < 0) {
        return null;
      }
      return {
        item_name,
        unit: String(row.unit || "").trim() || null,
        quantity,
        unit_price,
        amount: calcItemAmount(quantity, unit_price),
        note: String(row.note || "").trim() || null,
      };
    })
    .filter(Boolean);
}
